import React from "react";
import { motion } from "framer-motion";
import layerIcon from "../../assets/layer-icon.png";

interface HeroSectionProps {
  timeLeft: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
}

export const HeroSection: React.FC<HeroSectionProps> = ({ timeLeft }) => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    let raf = 0;
    let angle = 0;

    const draw = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.translate(canvas.width * 0.5, canvas.height * 0.5);
      ctx.rotate(angle);
      ctx.strokeStyle = "rgba(59, 158, 197, 0.07)";
      ctx.lineWidth = 2;
      const size = Math.min(canvas.width, canvas.height) * 0.28;
      ctx.strokeRect(-size, -size, size * 2, size * 2);
      ctx.rotate(Math.PI / 4);
      ctx.strokeRect(-size * 0.65, -size * 0.65, size * 1.3, size * 1.3);
      ctx.restore();
      angle += 0.0018;
      raf = requestAnimationFrame(draw);
    };

    draw();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <section
      id="home"
      className="relative w-full min-h-screen flex items-center overflow-hidden text-white"
      style={{ background: "linear-gradient(135deg, #1E3313 0%, #223F16 35%, #18280F 100%)" }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.05),rgba(0,0,0,0.35))]" />
      <div className="absolute left-[18%] top-[14%] w-[280px] h-[280px] rounded-full blur-[80px] opacity-40" style={{ backgroundColor: "#6CD62A" }} />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 lg:pt-0 pb-4 sm:pb-8 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_.8fr] gap-6 sm:gap-8 lg:gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:mt-20 space-y-10 sm:space-y-12 lg:space-y-6 text-center lg:text-left"
          >
            <div className="inline-flex items-center justify-center lg:justify-start gap-2 px-4 py-2 bg-white/10 backdrop-blur border border-[#D3E54A]/60 kapsul-badge mx-auto lg:mx-0">
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "#4CAF50" }} />
              <span className="text-white font-medium text-sm">11 Nisan 2026 · 10:00</span>
            </div>

            <div className="space-y-3">
              <h1 className="font-black text-4xl sm:text-6xl lg:text-8xl leading-none tracking-tight">
                TARIM
                <br />
                <span style={{ color: "#E8F974" }}>IDEATHONU</span>
              </h1>
              <div className="w-20 h-1 rounded-full mx-auto lg:mx-0" style={{ background: "linear-gradient(90deg,#73E03A,#D3E54A)" }} />
            </div>

            <p className="text-white/85 text-sm sm:text-base md:text-xl leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Konya Büyükşehir Belediyesi Kapsül Teknoloji Platformu ve MEVKA iş birliğinde düzenlenen
              Tarım Teknolojileri Ideathonu'na hoş geldiniz.
            </p>
            <div className="inline-flex items-center gap-3 rounded-2xl border border-white/20 bg-white/10 px-2 sm:px-4 py-1 backdrop-blur-sm">
              <img src={layerIcon} alt="Tarım ideathon ikonu" className="h-8 w-8 object-contain rounded-lg" />
              <span className="text-sm text-white/90">Konya Zindankale Yerleşkesi</span>
            </div>


            <div className="grid grid-cols-4 gap-2.5 pt-2 lg:hidden max-w-md mx-auto lg:mx-0 w-full">
              {[
                { label: "Gün", value: timeLeft.days },
                { label: "Saat", value: timeLeft.hours },
                { label: "Dakika", value: timeLeft.minutes },
                { label: "Saniye", value: timeLeft.seconds },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-white/25 bg-white/10 px-2 py-2 text-center backdrop-blur-sm"
                >
                  <div className="text-lg font-bold leading-none text-white">{item.value}</div>
                  <div className="mt-1 text-[10px] font-semibold text-white/90">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="hidden lg:grid grid-cols-3 gap-4 pt-2">
              {["Sektör Temsilcileri", "Uzman Jüri", "Sürdürülebilir Fikirler"].map((stat, index) => (
                <motion.div
                  key={stat}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -2, scale: 1.01 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, type: "spring", stiffness: 220, damping: 18 }}
                  className="kapsul-card py-4 text-center text-[var(--text-primary)] bg-white border-[#d8e1ef]"
                >
                  {stat}
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="hidden lg:block kapsul-card p-6 md:p-8 border-white/30 text-white shadow-[0_16px_40px_rgba(0,0,0,0.25)] backdrop-blur-xl"
            style={{ background: "linear-gradient(165deg, rgba(255,255,255,0.14), rgba(255,255,255,0.08))" }}
          >
            <p className="text-xs sm:text-sm uppercase tracking-wider text-white font-semibold drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]">
              Finale Kalan Süre
            </p>
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3 sm:mt-4">
              {[
                { label: "Gün", value: timeLeft.days },
                { label: "Saat", value: timeLeft.hours },
                { label: "Dakika", value: timeLeft.minutes },
                { label: "Saniye", value: timeLeft.seconds },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border p-2.5 sm:p-4 text-center border-white/30 backdrop-blur-sm"
                  style={{ background: "linear-gradient(155deg, rgba(255,255,255,0.14), rgba(255,255,255,0.08))" }}
                >
                  <div className="text-3xl sm:text-4xl font-extrabold leading-none text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.45)]">
                    {item.value}
                  </div>
                  <div className="mt-1 text-[10px] sm:text-xs uppercase tracking-wider text-white/95 font-semibold">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>

            <div
              className="mt-6 rounded-xl border p-4 border-white/30 backdrop-blur-sm"
              style={{ backgroundColor: "rgba(120,145,118,0.28)" }}
            >
              <p className="text-sm text-white/95 font-medium">Büyük Final</p>
              <p className="text-2xl font-extrabold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">11 Nisan 2026</p>
            </div>

            <div className="mt-4 flex items-center gap-2 rounded-xl border border-[#E8F974]/30 bg-[#E8F974]/10 px-4 py-3 backdrop-blur-sm">
              <div className="w-2 h-2 rounded-full bg-[#E8F974] shrink-0" />
              <p className="text-sm text-white/95 font-semibold">Tüm başvuru süreçleri tamamlanmıştır</p>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};
